import React, { forwardRef, useRef } from 'react';

// A plain textarea for editing an email template, with every {{placeholder}}
// coloured in place: the text itself sits in a transparent textarea on top of
// a mirrored copy that carries the colours (the usual "highlight within
// textarea" trick), so editing stays native - undo, selection, IME - while
// the admin can still see at a glance which parts the system fills in.
//
// Colours follow the preview's legend: yellow for values from the database,
// blue for ready-made blocks, red for a placeholder the template doesn't know.

export const PLACEHOLDER_PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9]*)\s*\}\}/g;

export const KIND_STYLE = {
  TEXT: { background: '#fde68a', boxShadow: '0 0 0 1px #f59e0b' },
  URL: { background: '#fde68a', boxShadow: '0 0 0 1px #f59e0b' },
  BLOCK: { background: '#bae6fd', boxShadow: '0 0 0 1px #0ea5e9' },
  UNKNOWN: { background: '#fecaca', boxShadow: '0 0 0 1px #dc2626' },
};

const shared = {
  margin: 0,
  padding: '.6rem .7rem',
  border: '1px solid #cbd5e1',
  font: '13px/1.55 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  letterSpacing: 'normal',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  boxSizing: 'border-box',
  width: '100%',
  tabSize: 2,
  // Same scrollbar room in both layers, so the text wraps at the same place.
  scrollbarGutter: 'stable',
};

const highlighted = (value, kindOf) => {
  const parts = [];
  let last = 0;
  value.replace(PLACEHOLDER_PATTERN, (match, key, offset) => {
    if (offset > last) parts.push(value.slice(last, offset));
    const kind = kindOf(key) || 'UNKNOWN';
    parts.push(<mark key={offset} style={{ color: 'transparent', borderRadius: 3, ...KIND_STYLE[kind] }}>{match}</mark>);
    last = offset + match.length;
    return match;
  });
  if (last < value.length) parts.push(value.slice(last));
  // A trailing newline needs something after it, or the mirror is a line short.
  parts.push('\n ');
  return parts;
};

const PlaceholderEditor = forwardRef(({ value, onChange, kindOf, rows = 20, singleLine = false, onFocus, ariaLabel, minHeight }, ref) => {
  const backdrop = useRef(null);
  const syncScroll = e => {
    if (backdrop.current) {
      backdrop.current.scrollTop = e.target.scrollTop;
      backdrop.current.scrollLeft = e.target.scrollLeft;
    }
  };
  const height = singleLine ? undefined : minHeight;
  return (
    <div style={{ position: 'relative', background: '#fff' }}>
      <div ref={backdrop} aria-hidden="true"
        style={{ ...shared, position: 'absolute', inset: 0, overflow: 'hidden', color: 'transparent', pointerEvents: 'none', borderColor: 'transparent' }}>
        {highlighted(value || '', kindOf)}
      </div>
      <textarea ref={ref} value={value} aria-label={ariaLabel} rows={singleLine ? 1 : rows} spellCheck={!singleLine}
        onFocus={onFocus} onScroll={syncScroll}
        onChange={e => onChange(singleLine ? e.target.value.replace(/[\r\n]+/g, ' ') : e.target.value)}
        onKeyDown={e => { if (singleLine && e.key === 'Enter') e.preventDefault(); }}
        style={{ ...shared, position: 'relative', display: 'block', background: 'transparent', color: '#0f172a',
          caretColor: '#0f172a', resize: singleLine ? 'none' : 'vertical', overflowY: singleLine ? 'hidden' : 'auto',
          minHeight: height }} />
    </div>
  );
});

export default PlaceholderEditor;
